import { useEffect, useState } from "react"
import { ChevronDown, ChevronRight, LoaderCircle } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { MultiSelect } from "@/components/ui/multi-select"
import { Switch } from "@/components/ui/switch"
import { cn } from "@/lib/utils"
import { useDocTypes } from "@/pages/doc-type-management/presentation/hooks/use-doc-types"
import type { UesConfig } from "@/pages/knowledge-bases/domain/entities/ues-config"
import { useEmbeddingModels } from "@/pages/knowledge-bases/presentation/hooks/use-embedding-models"
import { useKbUesConfig } from "@/pages/knowledge-bases/presentation/hooks/use-kb-ues-config"
import { useUpdateKbUesConfig } from "@/pages/knowledge-bases/presentation/hooks/use-update-kb-ues-config"
import type {
  CreateKnowledgeBaseInput,
  KnowledgeBase,
} from "@/pages/knowledge-bases/domain/entities/knowledge-base"
import { useUpdateKnowledgeBase } from "../hooks/use-update-knowledge-base"

type Props = {
  open: boolean
  kb: KnowledgeBase
  onClose: () => void
}

export const EditKbDialog = ({ open, kb, onClose }: Props) => {
  const { data: embeddingModels = [] } = useEmbeddingModels()
  const { data: docTypes = [] } = useDocTypes()
  const {
    data: uesConfig,
    isLoading: isUesLoading,
    isError: isUesError,
  } = useKbUesConfig(kb.id)
  const { mutateAsync: updateAsync, isPending: isKbPending } =
    useUpdateKnowledgeBase(kb.id)
  const { mutateAsync: updateUesAsync, isPending: isUesPending } =
    useUpdateKbUesConfig(kb.id)

  const [name, setName] = useState(kb.name)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [chunkSize, setChunkSize] = useState(kb.chunkingConfig.chunkSize)
  const [chunkOverlap, setChunkOverlap] = useState(kb.chunkingConfig.chunkOverlap)
  const [embeddingModel, setEmbeddingModel] = useState(kb.chunkingConfig.embeddingModelCode)
  const [uesEnabled, setUesEnabled] = useState(false)
  const [docTypeScope, setDocTypeScope] = useState<"all" | "specific">("all")
  const [selectedDocTypeIds, setSelectedDocTypeIds] = useState<string[]>([])
  const [uesValidation, setUesValidation] = useState<string | null>(null)

  const isPending = isKbPending || isUesPending

  useEffect(() => {
    if (!open) return
    setName(kb.name)
    setChunkSize(kb.chunkingConfig.chunkSize)
    setChunkOverlap(kb.chunkingConfig.chunkOverlap)
    setEmbeddingModel(kb.chunkingConfig.embeddingModelCode)
    setAdvancedOpen(false)
  }, [
    open,
    kb.id,
    kb.name,
    kb.chunkingConfig.chunkSize,
    kb.chunkingConfig.chunkOverlap,
    kb.chunkingConfig.embeddingModelCode,
  ])

  useEffect(() => {
    if (!open || !uesConfig) return
    setUesEnabled(uesConfig.enabled)
    setDocTypeScope(uesConfig.docTypeIds === "all" ? "all" : "specific")
    setSelectedDocTypeIds(
      uesConfig.docTypeIds === "all" ? [] : uesConfig.docTypeIds,
    )
    setUesValidation(null)
  }, [open, uesConfig])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return

    // The UES section is only in play once its config has loaded. If it failed
    // to load (or is still loading), saving the KB fields must not be blocked —
    // we simply skip the UES PUT rather than overwrite the server with defaults.
    const uesAvailable = !isUesLoading && !isUesError && uesConfig !== undefined
    if (
      uesAvailable &&
      docTypeScope === "specific" &&
      selectedDocTypeIds.length === 0
    ) {
      setUesValidation("Select at least one doc type.")
      return
    }

    const input: CreateKnowledgeBaseInput = {
      name: name.trim(),
      chunkingConfig: {
        chunkSize: chunkSize,
        chunkOverlap,
        embeddingModelCode: embeddingModel,
      },
    }

    // Two independent mutations run concurrently. On partial failure (one
    // persists, the other rejects) the dialog stays open and the global toast
    // fires; re-saving is idempotent and reconciles the state.
    const tasks: Promise<unknown>[] = [updateAsync(input)]
    if (uesAvailable) {
      const config: UesConfig = {
        enabled: uesEnabled,
        docTypeIds: docTypeScope === "all" ? "all" : selectedDocTypeIds,
      }
      tasks.push(updateUesAsync(config))
    }

    try {
      await Promise.all(tasks)
      onClose()
    } catch {
      // Global mutation onError already toasts; keep the dialog open.
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="grid-rows-[auto_minmax(0,1fr)_auto] max-h-[85vh] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Knowledge Base</DialogTitle>
        </DialogHeader>

        <form
          id="edit-kb-form"
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-col gap-4 overflow-y-auto"
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-kb-name">Name *</Label>
            <Input
              id="edit-kb-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={isPending}
            />
          </div>

          <section className="flex flex-col gap-3 rounded-lg border p-4">
            <h3 className="text-sm font-medium">UES</h3>

            {isUesLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <LoaderCircle className="size-4 animate-spin" />
                Loading UES configuration…
              </div>
            ) : isUesError ? (
              <p className="text-sm text-destructive">
                Unable to load UES configuration.
              </p>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <Label htmlFor="edit-kb-ues-enabled">Enabled</Label>
                  <Switch
                    id="edit-kb-ues-enabled"
                    checked={uesEnabled}
                    onCheckedChange={setUesEnabled}
                    disabled={isPending}
                  />
                </div>

                <fieldset className="flex flex-col gap-2">
                  <legend className="sr-only">Document type scope</legend>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="edit-kb-doc-type-scope"
                      value="all"
                      checked={docTypeScope === "all"}
                      onChange={() => {
                        setDocTypeScope("all")
                        setUesValidation(null)
                      }}
                      disabled={isPending}
                    />
                    All doc types
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="edit-kb-doc-type-scope"
                      value="specific"
                      checked={docTypeScope === "specific"}
                      onChange={() => setDocTypeScope("specific")}
                      disabled={isPending}
                    />
                    Specific
                  </label>
                </fieldset>

                {docTypeScope === "specific" && (
                  <div className="flex flex-col gap-1.5">
                    <MultiSelect
                      options={docTypes.map((docType) => ({
                        label: docType.displayName,
                        value: docType.id,
                      }))}
                      selected={selectedDocTypeIds}
                      onChange={(ids) => {
                        setSelectedDocTypeIds(ids)
                        if (ids.length > 0) setUesValidation(null)
                      }}
                      placeholder="Select doc types"
                      disabled={isPending}
                    />
                    {uesValidation && (
                      <p className="text-xs text-destructive">{uesValidation}</p>
                    )}
                  </div>
                )}
              </>
            )}
          </section>

          <div>
            <button
              type="button"
              className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
              onClick={() => setAdvancedOpen((v) => !v)}
            >
              {advancedOpen ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
              Advanced configuration
            </button>

            {advancedOpen && (
              <div className="mt-3 flex flex-col gap-4 rounded-lg border p-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="edit-kb-chunk-size">Chunk size</Label>
                  <Input
                    id="edit-kb-chunk-size"
                    type="number"
                    min={1}
                    step={1}
                    value={chunkSize}
                    onChange={(e) => setChunkSize(parseInt(e.target.value, 10) || 0)}
                    disabled={isPending}
                  />
                  <p className="text-xs text-muted-foreground">Detail chunk size in tokens, e.g. 128</p>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="edit-kb-chunk-overlap">Chunk overlap</Label>
                  <Input
                    id="edit-kb-chunk-overlap"
                    type="number"
                    min={0}
                    max={1}
                    step={0.01}
                    value={chunkOverlap}
                    onChange={(e) => setChunkOverlap(parseFloat(e.target.value))}
                    disabled={isPending}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="edit-kb-embedding-model">Embedding model</Label>
                  <select
                    id="edit-kb-embedding-model"
                    value={embeddingModel}
                    onChange={(e) => setEmbeddingModel(e.target.value)}
                    disabled={isPending || embeddingModels.length === 0}
                    className={cn(
                      "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none",
                      "focus:ring-1 focus:ring-ring",
                      "disabled:cursor-not-allowed disabled:opacity-50",
                    )}
                  >
                    {embeddingModels.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.display}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-kb-form"
            disabled={isPending || !name.trim()}
          >
            {isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
